import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { findDuplicateAbwesenheit } from '../abwesenheiten/server/findDuplicateAbwesenheit.js';
import { sendAbwesenheitCreationMail, sendLangzeitKrankheitMail } from '../abwesenheiten/server/absenceMails.js';
import { validate } from '@adornis/validation/decorators.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { and } from '@adornis/validation/functions/and.js';
import type { ValidationFunctionOptions } from '@adornis/validation/validator.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import type { InsertOneResult } from 'mongodb';
import { Person } from '../users/entities/person.js';
import { PartOfDayEnum } from './enums/abwesenheiten.js';
import { EntityClass } from './enums/class-names.js';
import { EntityCollection } from './enums/collection-names.js';
import { AbsenceApprovalStatus } from './enums/status.js';

registerEnum({ enum: PartOfDayEnum, name: 'PartOfDayEnum' });

/**
 * Abstract entity for absences.
 */
@Entity()
export class Abwesenheit extends MongoEntity {
  static override _class = 'Abwesenheit';
  static override _collectionName = EntityCollection.ABWESENHEITEN;

  @Field(type => Date, { default: v => v ?? new Date() })
  createdAt!: Date;

  @validate(sgNonOptional())
  @Field(type => ID)
  personID!: string;

  @Field(type => String, {
    resolve: function (this: Abwesenheit) {
      return async () => {
        const user = await getByID(Person, this.personID)({ profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } });
        return user?.profilDaten?.stammdaten?.vorname + ' ' + user?.profilDaten?.stammdaten?.nachname;
      };
    },
  })
  userDisplayName?: string;

  @validate(
    and(sgNonOptional(), (options: ValidationFunctionOptions<Date, Abwesenheit>) => {
      if (options.target.ende && options.value > options.target.ende)
        throw new ValidationError('Das Startdatum muss vorm Enddatum liegen!', { key: options.key });
    }),
  )
  @Field(type => Date)
  start: Maybe<Date>;

  @validate(sgNonOptional())
  @Field(type => Date)
  ende: Maybe<Date>;

  @Field(type => Boolean, { default: v => v ?? false })
  ganztaegig!: boolean;

  @Field(type => PartOfDayEnum, {
    // @ts-expect-error TODO this is correct
    default: v => v ?? PartOfDayEnum.VORMITTAG,
  })
  partOfTheDay!: Maybe<PartOfDayEnum>;

  /**
   * currently not in use
   */
  @validate(sgNonOptional())
  @Field(type => String, { default: v => v ?? AbsenceApprovalStatus.REQUESTED })
  genehmigungsStatus!: AbsenceApprovalStatus;

  @Field(type => String)
  kommentar?: string;

  static override async beforeCreate(entity: Abwesenheit): Promise<void> {
    await super.beforeCreate(entity);
    if (!entity.start || !entity.ende) return;

    const checkAbwesenheit = await findDuplicateAbwesenheit(
      entity.personID,
      entity.start,
      entity.ende,
      entity.partOfTheDay,
      entity._class,
    );
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (checkAbwesenheit)
      throw new ExpectedError(
        `Already existing abwesenheiten from ${entity.start} to ${entity.ende} for person id ${entity.personID}`,
      );
  }

  static override async afterCreate(entity: Abwesenheit, result: InsertOneResult<Document>): Promise<void> {
    await super.afterCreate(entity, result);
    try {
      await sendAbwesenheitCreationMail(entity._id);
    } catch (e) {
      logger.error(e, 'Fehler beim versucht die AbwesenheitCreationMail zu senden. (afterCreate)');
    }
    // Bei durchgehender Krankmeldung (KrankOhneAU/KrankMitAU) prüfen, ob die Langzeit-Schwelle
    // gerissen wurde und ggf. den Admin informieren.
    if (entity._class === KrankOhneAU._class || entity._class === KrankMitAU._class) {
      try {
        await sendLangzeitKrankheitMail(entity._id);
      } catch (e) {
        logger.error(e, 'Fehler beim Versuch die LangzeitKrankheitMail zu senden. (afterCreate)');
      }
    }
  }
}

/**
 * Vacations are a type of absence.
 */
@Entity()
export class Urlaub extends Abwesenheit {
  static override _class = EntityClass.URLAUB;

  static get someFields() {
    return selectionSet(() => this, 2, ['sonderUrlaub']);
  }

  @validate(sgNonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { default: v => v ?? false })
  sonderUrlaub!: boolean;
}

/**
 * Illnesses are a type of absence.
 * Contains additional logic to handle an attachment field.
 */
@Entity()
export class Krankheit extends Abwesenheit {
  static override _class = EntityClass.KRANKHEIT;

  @Field(type => String)
  attestID?: string;

  @Field(type => AdornisFile, {
    resolve: function (this: Krankheit) {
      return async (gqlFields: BaseQLSelectionSet<AdornisFile>) => {
        if (!this.attestID) return;
        return getByID(AdornisFile, this.attestID)(gqlFields);
      };
    },
  })
  attest?: AdornisFile;

  static override async beforeRemove(id: string, entityData: any): Promise<void> {
    await super.beforeRemove(id, entityData);
    // Delete associated files
    try {
      const krankheit = await getByID(Krankheit, id)({ attest: { _id: 1, _class: 1 } });
      await krankheit?.attest?.remove();
    } catch (e) {
      logger.error(e);
    }
  }
}

/**
 * @entity KindKrank
 */
@Entity()
export class KindKrank extends Abwesenheit {
  static override _class = EntityClass.KIND_KRANK;
}

/**
 * @entity Mutterschutz
 */
@Entity()
export class Mutterschutz extends Abwesenheit {
  static override _class = EntityClass.MUTTERSCHUTZ;
}

/**
 * @entity Elternzeit
 */
@Entity()
export class Elternzeit extends Abwesenheit {
  static override _class = EntityClass.ELTERNZEIT;
}

/**
 * @entity Sabbatical
 */
@Entity()
export class Sabbatical extends Abwesenheit {
  static override _class = EntityClass.SABBATICAL;
}

/**
 * @entity Altersteilzeit
 */
@Entity()
export class Altersteilzeit extends Abwesenheit {
  static override _class = EntityClass.ALTERSTEILZEIT;
}

/**
 * @entity UnbezahlterUrlaub
 */
@Entity()
export class UnbezahlterUrlaub extends Abwesenheit {
  static override _class = EntityClass.UNBEZAHLTER_URLAUB;
}

/**
 * @entity Beschaeftigungsverbot
 */
@Entity()
export class Beschaeftigungsverbot extends Abwesenheit {
  static override _class = EntityClass.BESCHAEFTIGUNGSVERBOT;
}

/**
 * @entity Fortbildung
 */
@Entity()
export class Fortbildung extends Abwesenheit {
  static override _class = EntityClass.FORTBILDUNG;
}

/**
 * @entity DienstlicheBeurlaubung
 */
@Entity()
export class DienstlicheBeurlaubung extends Abwesenheit {
  static override _class = EntityClass.DIENSTLICHE_BEURLAUBUNG;
}

/**
 * @entity Klassenfahrt
 */
@Entity()
export class Klassenfahrt extends Abwesenheit {
  static override _class = EntityClass.KLASSENFAHRT;
}

/**
 * @entity Exkursion
 */
@Entity()
export class Exkursion extends Abwesenheit {
  static override _class = EntityClass.EXKURSION;
}

/**
 * @entity DienstAmAnderenOrt
 */
@Entity()
export class DienstAmAnderenOrt extends Abwesenheit {
  static override _class = EntityClass.DIENST_AM_ANDEREN_ORT;
}

/**
 * @entity Lehrprobe
 */
@Entity()
export class Lehrprobe extends Abwesenheit {
  static override _class = EntityClass.LEHRPROBE;
}

/**
 * @entity FreieStudien
 */
@Entity()
export class InternerEinsatz extends Abwesenheit {
  static override _class = EntityClass.INTERNER_EINSATZ;
}

/**
 * @entity Sozialpraktikum
 */
@Entity()
export class Sozialpraktikum extends Abwesenheit {
  static override _class = EntityClass.SOZIALPRAKTIKUM;
}

/**
 * @entity FreieStudien
 */
@Entity()
export class FreieStudien extends Abwesenheit {
  static override _class = EntityClass.FREIE_STUDIEN;
}

/**
 * @entity Abiturprufungen
 */
@Entity()
export class Abiturprufungen extends Abwesenheit {
  static override _class = EntityClass.ABITURPRUFUNGEN;
}

/**
 * @entity ElternzeitTeilzeit
 */
@Entity()
export class ElternzeitTeilzeit extends Abwesenheit {
  static override _class = EntityClass.ELTERNZEIT_TEILZEIT;
}

/**
 * @entity AltersteilzeitAktiv
 */
@Entity()
export class AltersteilzeitAktiv extends Abwesenheit {
  static override _class = EntityClass.ALTERSTEILZEIT_AKTIV;
}

/**
 * @entity AltersteilzeitPassiv
 */
@Entity()
export class AltersteilzeitPassiv extends Abwesenheit {
  static override _class = EntityClass.ALTERSTEILZEIT_PASSIV;
}

/**
 * @entity VollFreigestelltPflege
 */
@Entity()
export class VollFreigestelltPflege extends Abwesenheit {
  static override _class = EntityClass.VOLL_FREIGESTELLT_PFLEGE;
}

/**
 * @entity VolleErwerbsminderungsRente
 */
@Entity()
export class VolleErwerbsminderungsRente extends Abwesenheit {
  static override _class = EntityClass.VOLLE_ERWERBSMINDERUNGS_RENTE;
}

/**
 * @entity VolleErwerbsminderungsRente
 */
@Entity()
export class Sonderurlaub extends Abwesenheit {
  static override _class = EntityClass.SONDERURLAUB;
}

/**
 * @entity Reha
 */
@Entity()
export class Reha extends Abwesenheit {
  static override _class = EntityClass.REHA;
}

/**
 * @entity KrankOhneAU
 */
@Entity()
export class KrankOhneAU extends Abwesenheit {
  static override _class = EntityClass.KRANKHEIT_OHNE_AU;
}

/**
 * @entity KrankMitAU
 */
@Entity()
export class KrankMitAU extends Abwesenheit {
  static override _class = EntityClass.KRANKHEIT_MIT_AU;
}

export function getLodasAbwesenheitsKey(className: string): string {
  switch (className) {
    case KindKrank._class:
      return '11';
    case VollFreigestelltPflege._class:
      return '14';
    case 'Pflegezeit mit vollständiger Freistellung':
      return '15';
    case 'Kurzzeitige Pflege':
      return '16';
    case 'unwiderrufliche unbezahlte Freistellung':
      return '19';
    case UnbezahlterUrlaub._class:
      return '21';
    case 'Unbezahlte Fehlzeit':
      return '22';
    case 'Unrechtmäßiger Streik':
      return '23';
    case 'Kinderpflege ohne Krankengeld':
      return '24';
    case 'Krank unentschldigt oder so':
      return '25';
    case 'Krank über 6 Wochen bei Erwerbsminderungs-/Altersrente':
      return '26';
    case 'Krank über 6 Wochen geringfügig entlohnter AN.':
      return '27';
    case 'Aussteuerung':
      return '28';
    case VolleErwerbsminderungsRente._class:
      return '29';
    case 'Rechtmäßiger Streik':
      return '31';
    case 'Aussperrung':
      return '32';
    case 'Entgeltfortzahlung ohne AU-Bescheinigung':
      return '33';
    case 'irgendwas':
      return '41';
    case 'Verletztengeld bei Krankheit oder Kur':
      return '42';
    case 'Übergangsgeld bei Krankheit oder Kur':
      return '43';
    case 'Versorgungskrankengeld bei Krankheit oder Kur':
      return '44';
    case Mutterschutz._class:
      return '45';
    case 'Krank bei Eintritt ohne Entgeltfortzahlung':
      return '47';
    case Elternzeit._class:
      return '51';
    case 'Wehrdienst':
      return '61';
    case 'Zivildienst':
      return '62';
    case 'Wehrübung':
      return '63';
    case 'Eignungsübung':
      return '64';
    case 'Kug/S-Kug Krank':
      return '81';
    case Krankheit._class:
      return '103';
    case 'unwiderrufliche bezahlte Freistellung':
      return '105';
    case 'Studienzeit (dualer Studiengang)':
      return '106';
    case 'Kind ist krank':
      return '200';
    case 'Kinderpflege bei Schwersterkrankung':
      return '201';
    case 'Pflegeunterstützungsgeld':
      return '210';
    case 'Ende Bezug Krankengeld/Beginn Bezug Arbeitslosengeld':
      return '260';
    case 'Entgeltfortzahlung wegen Organ-/Gewebespende':
      return '300';
    case 'Freistellung im Rahmen sonstiger flexibler Arbeitszeitregelungen':
      return '301';
    case 'Krankengeld bei Organ-/Blut/Gewebespende ohne EEL':
      return '302';
    case 'Entschädigungszahlung wegen Quarantäne':
      return '303';
    case 'Bezahlte Freistellung wegen angeordneter Quarantäne':
      return '304';
    case 'Bezahlte Freistellung wegen freiwilliger Quarantäne':
      return '305';
    case 'Unbezahlte Freistellung wegen freiwilliger Quarantäne':
      return '306';
    case 'Entschädigungszahlung nach IfSG wegen Beaufsichtigung Kind':
      return '307';
    case Beschaeftigungsverbot._class:
      return '308';
    case 'Vorsorgliche Einstellung der beruflichen Tätigkeit':
      return '309';
    case 'Krankengeld bei Mitaufnahme ins Krankenhaus':
      return '310';
    default:
      return '';
  }
}
